// The stays results page and the property page (pages/Hotels.jsx,
// pages/HotelDetail.jsx, pages/hotels/*, components/HotelCard.jsx,
// components/PropertyGallery.jsx). Kept apart from i18n/stays.js, which the
// Home hero loads with the first page: these tables travel only with the
// lazily loaded pages that use them.
import { plural } from "@/lib/format";

// Phase 4.1: the results page, the result card, the map and the filters
// (pages/Hotels.jsx, pages/hotels/*, components/HotelCard.jsx).
const staysCount = (lang, forms) => (n) => plural(n, forms, { lang });
const AR = {
  stays: { zero: "# مكان إقامة", one: "مكان إقامة واحد", two: "مكانا إقامة", few: "# أماكن إقامة", many: "# مكان إقامة", other: "# مكان إقامة" },
  nights: { zero: "# ليلة", one: "ليلة واحدة", two: "ليلتان", few: "# ليالٍ", many: "# ليلة", other: "# ليلة" },
  results: { zero: "# نتيجة", one: "نتيجة واحدة", two: "نتيجتين", few: "# نتائج", many: "# نتيجة", other: "# نتيجة" },
  reviews: { zero: "# تقييم موثق", one: "تقييم موثق واحد", two: "تقييمان موثقان", few: "# تقييمات موثقة", many: "# تقييمًا موثقًا", other: "# تقييم موثق" },
  rooms: { zero: "# غرفة", one: "غرفة واحدة", two: "غرفتان", few: "# غرف", many: "# غرفة", other: "# غرفة" },
};

export const staysUiL = {
  en: {
    title: (n, city) => `${staysCount("en", { one: "# stay", other: "# stays" })(n)}${city ? ` in ${city}` : ""}`,
    searching: "Looking for stays…",
    heading: (city) => (city ? `Stays in ${city}` : "Stays"),
    summary: { label: "Your search", modify: "Modify", anyDates: "Any dates", jump: "Change the search" },
    sortLabel: "Sort",
    sort: { recommended: "Default order", price: "Lowest total", priceNight: "Lowest price per night", rating: "Guest rating", reviews: "Most reviews" },
    sortWhy: {
      recommended: "Stays their owners list directly on Toukky come first, then our suppliers' stays.",
      price: "From the lowest to the highest total for your dates.",
      priceNight: "From the lowest to the highest price per night.",
      rating: "Average of verified Toukky reviews. Stays without reviews come after.",
      reviews: "Stays with the most verified reviews first.",
    },
    filters: {
      title: "Filters", reset: "Reset", loading: "Loading filters…", conditions: "Conditions", budgetNight: "Budget per night",
      show: (n) => `Show ${staysCount("en", { one: "# result", other: "# results" })(n)}`,
      budgetTotal: (nights) => `Total budget (${staysCount("en", { one: "# night", other: "# nights" })(nights)})`,
    },
    card: {
      outOf: "/5", noReviews: "No reviews yet", seeRooms: "See rooms", seeHome: "See this home",
      reviews: (n) => staysCount("en", { one: "# verified review", other: "# verified reviews" })(n),
      seeLabel: (cta, name) => `${cta}: ${name}`,
      perNightOnly: "per night. Add your dates for the total",
      rooms: (n) => staysCount("en", { one: "# room", other: "# rooms" })(n),
    },
    terms: {
      freeUntil: (when) => `Free cancellation until ${when}`,
      closed: "No free cancellation for these dates", nonRefundable: "Non-refundable after those 24 hours",
    },
    empty: {
      title: (city) => (city ? `No stays in ${city} for this search` : "No stays for this search"),
      body: "Try other dates or a nearby city.",
      filtered: (n) => `${staysCount("en", { one: "# stay matches", other: "# stays match" })(n)} your search, but none matches your filters.`,
      clear: "Clear the filters", change: "Change the search",
      filteredTitle: "No stay matches your filters",
    },
    error: { title: "Stays didn’t load", body: "Check your connection, then try again." },
    map: {
      region: "Map of the results", show: "Show the map", list: "List",
      approx: "Private homes: shown by neighbourhood. The exact address comes after a confirmed and paid booking.",
      exact: "Properties: exact addresses visible before booking.",
      mixed: "Properties: exact address. Private homes: neighbourhood only, exact address after a confirmed and paid booking.",
    },
    addDates: "Add your dates to see each stay’s total.",
    flightNext: (from) => (from ? `Choose your stay first: your flight from ${from} is offered right after, on the same dates.` : "Choose your stay first: your flight is offered right after, on the same dates."),
  },
  fr: {
    title: (n, city) => `${staysCount("fr", { one: "# séjour", other: "# séjours" })(n)}${city ? ` à ${city}` : ""}`,
    searching: "Recherche des séjours…",
    heading: (city) => (city ? `Séjours à ${city}` : "Séjours"),
    summary: { label: "Votre recherche", modify: "Modifier", anyDates: "Dates libres", jump: "Modifier la recherche" },
    sortLabel: "Trier",
    sort: { recommended: "Ordre par défaut", price: "Total le plus bas", priceNight: "Prix par nuit le plus bas", rating: "Note des voyageurs", reviews: "Le plus d’avis" },
    sortWhy: {
      recommended: "Les logements que leurs propriétaires proposent directement sur Toukky d’abord, puis ceux de nos fournisseurs.",
      price: "Du total le plus bas au plus élevé pour vos dates.",
      priceNight: "Du prix par nuit le plus bas au plus élevé.",
      rating: "Moyenne des avis vérifiés Toukky. Les logements sans avis viennent ensuite.",
      reviews: "Les logements qui ont le plus d’avis vérifiés d’abord.",
    },
    filters: {
      title: "Filtres", reset: "Réinitialiser", loading: "Chargement des filtres…", conditions: "Conditions", budgetNight: "Budget par nuit",
      show: (n) => `Afficher ${staysCount("fr", { one: "# résultat", other: "# résultats" })(n)}`,
      budgetTotal: (nights) => `Budget total (${staysCount("fr", { one: "# nuit", other: "# nuits" })(nights)})`,
    },
    card: {
      outOf: "/5", noReviews: "Pas encore d’avis", seeRooms: "Voir les chambres", seeHome: "Voir le logement",
      reviews: (n) => staysCount("fr", { one: "# avis vérifié", other: "# avis vérifiés" })(n),
      seeLabel: (cta, name) => `${cta} : ${name}`,
      perNightOnly: "par nuit. Ajoutez vos dates pour le total",
      rooms: (n) => staysCount("fr", { one: "# chambre", other: "# chambres" })(n),
    },
    terms: {
      freeUntil: (when) => `Annulation gratuite jusqu’au ${when}`,
      closed: "Pas d’annulation gratuite pour ces dates", nonRefundable: "Non remboursable passées ces 24 h",
    },
    empty: {
      title: (city) => (city ? `Aucun séjour à ${city} pour cette recherche` : "Aucun séjour pour cette recherche"),
      body: "Essayez d’autres dates ou une ville proche.",
      filtered: (n) => `${staysCount("fr", { one: "# séjour correspond", other: "# séjours correspondent" })(n)} à votre recherche, mais aucun à vos filtres.`,
      clear: "Effacer les filtres", change: "Modifier la recherche",
      filteredTitle: "Aucun séjour ne correspond à vos filtres",
    },
    error: { title: "Les séjours n’ont pas pu se charger", body: "Vérifiez votre connexion, puis réessayez." },
    map: {
      region: "Carte des résultats", show: "Voir la carte", list: "Liste",
      approx: "Logements privés : quartier indiqué. Adresse exacte après réservation confirmée et payée.",
      exact: "Établissements : adresses exactes visibles avant réservation.",
      mixed: "Établissements : adresse exacte. Logements privés : quartier seulement, adresse exacte après réservation confirmée et payée.",
    },
    addDates: "Ajoutez vos dates pour voir le total de chaque séjour.",
    flightNext: (from) => (from ? `Choisissez d’abord votre séjour : votre vol depuis ${from} vous est proposé juste après, aux mêmes dates.` : "Choisissez d’abord votre séjour : votre vol vous est proposé juste après, aux mêmes dates."),
  },
  es: {
    title: (n, city) => `${staysCount("es", { one: "# alojamiento", other: "# alojamientos" })(n)}${city ? ` en ${city}` : ""}`,
    searching: "Buscando alojamientos…",
    heading: (city) => (city ? `Alojamientos en ${city}` : "Alojamientos"),
    summary: { label: "Tu búsqueda", modify: "Modificar", anyDates: "Fechas libres", jump: "Cambiar la búsqueda" },
    sortLabel: "Ordenar",
    sort: { recommended: "Orden predeterminado", price: "Total más bajo", priceNight: "Precio por noche más bajo", rating: "Puntuación de los huéspedes", reviews: "Más opiniones" },
    sortWhy: {
      recommended: "Primero los alojamientos que sus propietarios publican directamente en Toukky, después los de nuestros proveedores.",
      price: "Del total más bajo al más alto para tus fechas.",
      priceNight: "Del precio por noche más bajo al más alto.",
      rating: "Media de las opiniones verificadas de Toukky. Los alojamientos sin opiniones van después.",
      reviews: "Primero los alojamientos con más opiniones verificadas.",
    },
    filters: {
      title: "Filtros", reset: "Restablecer", loading: "Cargando los filtros…", conditions: "Condiciones", budgetNight: "Presupuesto por noche",
      show: (n) => `Mostrar ${staysCount("es", { one: "# resultado", other: "# resultados" })(n)}`,
      budgetTotal: (nights) => `Presupuesto total (${staysCount("es", { one: "# noche", other: "# noches" })(nights)})`,
    },
    card: {
      outOf: "/5", noReviews: "Aún sin opiniones", seeRooms: "Ver habitaciones", seeHome: "Ver el alojamiento",
      reviews: (n) => staysCount("es", { one: "# opinión verificada", other: "# opiniones verificadas" })(n),
      seeLabel: (cta, name) => `${cta}: ${name}`,
      perNightOnly: "por noche. Añade tus fechas para ver el total",
      rooms: (n) => staysCount("es", { one: "# habitación", other: "# habitaciones" })(n),
    },
    terms: {
      freeUntil: (when) => `Cancelación gratuita hasta el ${when}`,
      closed: "Sin cancelación gratuita para estas fechas", nonRefundable: "No reembolsable pasadas esas 24 h",
    },
    empty: {
      title: (city) => (city ? `Ningún alojamiento en ${city} para esta búsqueda` : "Ningún alojamiento para esta búsqueda"),
      body: "Prueba otras fechas o una ciudad cercana.",
      filtered: (n) => `${staysCount("es", { one: "# alojamiento coincide", other: "# alojamientos coinciden" })(n)} con tu búsqueda, pero ninguno con tus filtros.`,
      clear: "Borrar los filtros", change: "Cambiar la búsqueda",
      filteredTitle: "Ningún alojamiento coincide con tus filtros",
    },
    error: { title: "No se pudieron cargar los alojamientos", body: "Comprueba tu conexión y vuelve a intentarlo." },
    map: {
      region: "Mapa de los resultados", show: "Ver el mapa", list: "Lista",
      approx: "Viviendas privadas: se indica el barrio. La dirección exacta llega tras confirmar y pagar la reserva.",
      exact: "Establecimientos: direcciones exactas visibles antes de reservar.",
      mixed: "Establecimientos: dirección exacta. Viviendas privadas: solo el barrio, dirección exacta tras confirmar y pagar la reserva.",
    },
    addDates: "Añade tus fechas para ver el total de cada alojamiento.",
    flightNext: (from) => (from ? `Elige primero tu alojamiento: tu vuelo desde ${from} se te propone justo después, en las mismas fechas.` : "Elige primero tu alojamiento: tu vuelo se te propone justo después, en las mismas fechas."),
  },
  pt: {
    title: (n, city) => `${staysCount("pt", { one: "# alojamento", other: "# alojamentos" })(n)}${city ? ` em ${city}` : ""}`,
    searching: "A procurar alojamentos…",
    heading: (city) => (city ? `Alojamentos em ${city}` : "Alojamentos"),
    summary: { label: "A sua pesquisa", modify: "Alterar", anyDates: "Datas livres", jump: "Alterar a pesquisa" },
    sortLabel: "Ordenar",
    sort: { recommended: "Ordem predefinida", price: "Total mais baixo", priceNight: "Preço por noite mais baixo", rating: "Avaliação dos hóspedes", reviews: "Mais avaliações" },
    sortWhy: {
      recommended: "Primeiro os alojamentos que os proprietários publicam diretamente na Toukky, depois os dos nossos fornecedores.",
      price: "Do total mais baixo ao mais alto para as suas datas.",
      priceNight: "Do preço por noite mais baixo ao mais alto.",
      rating: "Média das avaliações verificadas da Toukky. Os alojamentos sem avaliações vêm depois.",
      reviews: "Primeiro os alojamentos com mais avaliações verificadas.",
    },
    filters: {
      title: "Filtros", reset: "Repor", loading: "A carregar os filtros…", conditions: "Condições", budgetNight: "Orçamento por noite",
      show: (n) => `Mostrar ${staysCount("pt", { one: "# resultado", other: "# resultados" })(n)}`,
      budgetTotal: (nights) => `Orçamento total (${staysCount("pt", { one: "# noite", other: "# noites" })(nights)})`,
    },
    card: {
      outOf: "/5", noReviews: "Ainda sem avaliações", seeRooms: "Ver quartos", seeHome: "Ver o alojamento",
      reviews: (n) => staysCount("pt", { one: "# avaliação verificada", other: "# avaliações verificadas" })(n),
      seeLabel: (cta, name) => `${cta}: ${name}`,
      perNightOnly: "por noite. Adicione as datas para ver o total",
      rooms: (n) => staysCount("pt", { one: "# quarto", other: "# quartos" })(n),
    },
    terms: {
      freeUntil: (when) => `Cancelamento gratuito até ${when}`,
      closed: "Sem cancelamento gratuito para estas datas", nonRefundable: "Não reembolsável após essas 24 h",
    },
    empty: {
      title: (city) => (city ? `Nenhum alojamento em ${city} para esta pesquisa` : "Nenhum alojamento para esta pesquisa"),
      body: "Experimente outras datas ou uma cidade próxima.",
      filtered: (n) => `${staysCount("pt", { one: "# alojamento corresponde", other: "# alojamentos correspondem" })(n)} à sua pesquisa, mas nenhum aos seus filtros.`,
      clear: "Limpar os filtros", change: "Alterar a pesquisa",
      filteredTitle: "Nenhum alojamento corresponde aos seus filtros",
    },
    error: { title: "Não foi possível carregar os alojamentos", body: "Verifique a ligação e tente novamente." },
    map: {
      region: "Mapa dos resultados", show: "Ver o mapa", list: "Lista",
      approx: "Residências privadas: indicado o bairro. O endereço exato chega após a confirmação e o pagamento da reserva.",
      exact: "Estabelecimentos: endereços exatos visíveis antes da reserva.",
      mixed: "Estabelecimentos: endereço exato. Residências privadas: apenas o bairro, endereço exato após confirmar e pagar a reserva.",
    },
    addDates: "Adicione as suas datas para ver o total de cada alojamento.",
    flightNext: (from) => (from ? `Escolha primeiro a sua estadia: o seu voo de ${from} é-lhe proposto logo a seguir, nas mesmas datas.` : "Escolha primeiro a sua estadia: o seu voo é-lhe proposto logo a seguir, nas mesmas datas."),
  },
  ar: {
    title: (n, city) => `${staysCount("ar", AR.stays)(n)}${city ? ` في ${city}` : ""}`,
    searching: "جارٍ البحث عن أماكن الإقامة…",
    heading: (city) => (city ? `أماكن الإقامة في ${city}` : "أماكن الإقامة"),
    summary: { label: "بحثك", modify: "تعديل", anyDates: "تواريخ مرنة", jump: "تعديل البحث" },
    sortLabel: "ترتيب",
    sort: { recommended: "الترتيب الافتراضي", price: "أقل إجمالي", priceNight: "أقل سعر لليلة", rating: "تقييم النزلاء", reviews: "الأكثر تقييمًا" },
    sortWhy: {
      recommended: "أماكن الإقامة التي يعرضها أصحابها مباشرة على Toukky أولًا، ثم أماكن موردينا.",
      price: "من أقل إجمالي إلى أعلاه لتواريخك.",
      priceNight: "من أقل سعر لليلة إلى أعلاه.",
      rating: "متوسط التقييمات الموثقة على Toukky. تأتي أماكن الإقامة بلا تقييمات بعدها.",
      reviews: "أماكن الإقامة ذات أكبر عدد من التقييمات الموثقة أولًا.",
    },
    filters: {
      title: "عوامل التصفية", reset: "إعادة الضبط", loading: "جارٍ تحميل عوامل التصفية…", conditions: "الشروط", budgetNight: "الميزانية لكل ليلة",
      show: (n) => `عرض ${staysCount("ar", AR.results)(n)}`,
      budgetTotal: (nights) => `الميزانية الإجمالية (${staysCount("ar", AR.nights)(nights)})`,
    },
    card: {
      outOf: "/5", noReviews: "لا توجد تقييمات بعد", seeRooms: "عرض الغرف", seeHome: "عرض المسكن",
      reviews: (n) => staysCount("ar", AR.reviews)(n),
      seeLabel: (cta, name) => `${cta}: ${name}`,
      perNightOnly: "لكل ليلة. أضف تواريخك لرؤية الإجمالي",
      rooms: (n) => staysCount("ar", AR.rooms)(n),
    },
    terms: {
      freeUntil: (when) => `إلغاء مجاني حتى ${when}`,
      closed: "لا يوجد إلغاء مجاني لهذه التواريخ", nonRefundable: "غير قابل للاسترداد بعد هذه الساعات الـ24",
    },
    empty: {
      title: (city) => (city ? `لا توجد أماكن إقامة في ${city} لهذا البحث` : "لا توجد أماكن إقامة لهذا البحث"),
      body: "جرّب تواريخ أخرى أو مدينة قريبة.",
      filtered: (n) => `يطابق بحثك ${staysCount("ar", AR.stays)(n)}، لكن لا شيء يطابق عوامل التصفية.`,
      clear: "مسح عوامل التصفية", change: "تعديل البحث",
      filteredTitle: "لا توجد أماكن إقامة تطابق عوامل التصفية",
    },
    error: { title: "تعذر تحميل أماكن الإقامة", body: "تحقق من اتصالك، ثم حاول مرة أخرى." },
    map: {
      region: "خريطة النتائج", show: "عرض الخريطة", list: "القائمة",
      approx: "المساكن الخاصة: يُعرض الحي فقط. يتاح العنوان الدقيق بعد تأكيد الحجز ودفعه.",
      exact: "المنشآت: العناوين الدقيقة متاحة قبل الحجز.",
      mixed: "المنشآت: العنوان الدقيق. المساكن الخاصة: الحي فقط، والعنوان الدقيق بعد تأكيد الحجز ودفعه.",
    },
    addDates: "أضف تواريخك لرؤية إجمالي كل إقامة.",
    flightNext: (from) => (from ? `اختر إقامتك أولاً: نقترح عليك رحلتك من ${from} مباشرة بعد ذلك، في التواريخ نفسها.` : "اختر إقامتك أولاً: نقترح عليك رحلتك مباشرة بعد ذلك، في التواريخ نفسها."),
  },
  zh: {
    title: (n, city) => (city ? `${city}的 ${n} 处住宿` : `${n} 处住宿`),
    searching: "正在查找住宿…",
    heading: (city) => (city ? `${city}的住宿` : "住宿"),
    summary: { label: "您的搜索", modify: "修改", anyDates: "日期不限", jump: "修改搜索" },
    sortLabel: "排序",
    sort: { recommended: "默认排序", price: "总价最低", priceNight: "每晚价格最低", rating: "住客评分", reviews: "点评最多" },
    sortWhy: {
      recommended: "房东直接在 Toukky 发布的住宿排在前面，其后是供应商的住宿。",
      price: "按您所选日期的总价从低到高排列。",
      priceNight: "按每晚价格从低到高排列。",
      rating: "Toukky 已验证点评的平均分。没有点评的住宿排在后面。",
      reviews: "已验证点评最多的住宿排在前面。",
    },
    filters: {
      title: "筛选", reset: "重置", loading: "正在加载筛选条件…", conditions: "条件", budgetNight: "每晚预算",
      show: (n) => `显示 ${n} 个结果`,
      budgetTotal: (nights) => `总预算（${nights} 晚）`,
    },
    card: {
      outOf: "/5", noReviews: "暂无点评", seeRooms: "查看客房", seeHome: "查看住宿",
      reviews: (n) => `${n} 条已验证点评`,
      seeLabel: (cta, name) => `${cta}：${name}`,
      perNightOnly: "每晚。添加日期即可查看总价",
      rooms: (n) => `${n} 间客房`,
    },
    terms: {
      freeUntil: (when) => `${when} 前可免费取消`,
      closed: "这些日期不可免费取消", nonRefundable: "超过这 24 小时后不可退款",
    },
    empty: {
      title: (city) => (city ? `${city}没有符合此搜索的住宿` : "没有符合此搜索的住宿"),
      body: "请尝试其他日期或附近的城市。",
      filtered: (n) => `有 ${n} 处住宿符合您的搜索，但没有符合筛选条件的住宿。`,
      clear: "清除筛选", change: "修改搜索",
      filteredTitle: "没有符合筛选条件的住宿",
    },
    error: { title: "无法加载住宿", body: "请检查网络连接，然后重试。" },
    map: {
      region: "结果地图", show: "查看地图", list: "列表",
      approx: "私人住宅仅显示所在街区，预订确认并付款后提供准确地址。",
      exact: "经营性住宿的准确地址在预订前即可查看。",
      mixed: "经营性住宿：准确地址。私人住宅：仅显示街区，预订确认并付款后提供准确地址。",
    },
    addDates: "添加日期即可查看每处住宿的总价。",
    flightNext: (from) => (from ? `请先选择住宿：随后将按相同日期为您推荐从${from}出发的航班。` : "请先选择住宿：随后将按相同日期为您推荐航班。"),
  },
};

// Phase 4.1: the property page's booking card, rooms, states and photo
// gallery (pages/HotelDetail.jsx, pages/hotels/PropertySections.jsx,
// pages/hotels/HomeStaySections.jsx, components/PropertyGallery.jsx).
export const propertyPageL = {
  en: {
    notFound: { title: "This stay is no longer online", body: "It may have been removed. Other stays are waiting.", action: "See stays" },
    loadError: { title: "This stay didn’t load", body: "Check your connection, then try again." },
    seeRooms: "See rooms", seeOffer: "See the offer",
    sections: "Sections of the property", location: "Location",
    booking: {
      label: "Your stay", from: "From", perNight: "per night", total: "Total",
      chooseDates: "Choose your dates", addDates: "Add your dates to see the total for your stay.",
      line: (price, nights) => `${price} × ${nights}`, roomsLine: (rooms, type) => (type ? `${rooms}, ${type}` : rooms),
      maxGuests: (n) => `This place takes ${n} guest${n > 1 ? "s" : ""} at most. Change the number of guests to book.`,
      full: "This room has no availability left. Choose another room.", allFull: "No room is left at this stay for now.",
      promotion: "If the owner runs a promotion on these dates, it is taken off at the next step.",
      datesInvalid: "Check-out must be after check-in.",
      converted: (amount) => `≈ ${amount} in your currency, converted from the CFA franc price above.`,
    },
    rooms: {
      none: "This property hasn’t described its rooms yet. Choose your dates and guests in the summary to book it.",
      header: { room: "Room", terms: "Conditions", price: "Price for your stay" },
      choose: "Choose", chosen: "Chosen", fits: (n) => `Sleeps ${n}`,
      noTerms: "Conditions shown before payment.", forNights: (nights) => `for ${nights}`,
    },
    amenitiesAll: "Show all amenities", amenitiesNone: "The property hasn’t listed its facilities yet.",
    policiesNone: "The property hasn’t given its conditions yet.",
    gallery: { open: "Open photo", previous: "Previous photo", next: "Next photo", all: "See all photos", none: "No photos yet", title: "Photo gallery", thumbnails: "Thumbnails", photo: "Photo" },
  },
  fr: {
    notFound: { title: "Ce logement n’est plus en ligne", body: "Il a peut-être été retiré. D’autres séjours vous attendent.", action: "Voir les séjours" },
    loadError: { title: "Ce logement n’a pas pu se charger", body: "Vérifiez votre connexion, puis réessayez." },
    seeRooms: "Voir les chambres", seeOffer: "Voir l’offre",
    sections: "Sections du logement", location: "Emplacement",
    booking: {
      label: "Votre séjour", from: "À partir de", perNight: "par nuit", total: "Total",
      chooseDates: "Choisir les dates", addDates: "Ajoutez vos dates pour voir le total de votre séjour.",
      line: (price, nights) => `${price} × ${nights}`, roomsLine: (rooms, type) => (type ? `${rooms}, ${type}` : rooms),
      maxGuests: (n) => `Ce logement accueille ${n} voyageur${n > 1 ? "s" : ""} au maximum. Changez le nombre de voyageurs pour réserver.`,
      full: "Cette chambre est complète. Choisissez-en une autre.", allFull: "Plus aucune chambre n’est disponible dans ce logement pour l’instant.",
      promotion: "Si le propriétaire a une promotion sur ces dates, elle est déduite à l’étape suivante.",
      datesInvalid: "Le départ doit être après l’arrivée.",
      converted: (amount) => `≈ ${amount} dans votre devise, converti depuis le prix en francs CFA ci-dessus.`,
    },
    rooms: {
      none: "Cet hébergement n’a pas encore détaillé ses chambres. Choisissez vos dates et vos voyageurs dans le récapitulatif pour le réserver.",
      header: { room: "Chambre", terms: "Conditions", price: "Prix pour votre séjour" },
      choose: "Choisir", chosen: "Choisie", fits: (n) => `${n} voyageur${n > 1 ? "s" : ""}`,
      noTerms: "Conditions indiquées avant le paiement.", forNights: (nights) => `pour ${nights}`,
    },
    amenitiesAll: "Voir tous les équipements", amenitiesNone: "L’établissement n’a pas encore indiqué ses équipements.",
    policiesNone: "L’établissement n’a pas encore indiqué ses conditions.",
    gallery: { open: "Ouvrir la photo", previous: "Photo précédente", next: "Photo suivante", all: "Voir toutes les photos", none: "Pas encore de photos", title: "Galerie photos", thumbnails: "Miniatures", photo: "Photo" },
  },
  es: {
    notFound: { title: "Este alojamiento ya no está publicado", body: "Puede que se haya retirado. Hay otros alojamientos esperándote.", action: "Ver alojamientos" },
    loadError: { title: "No se pudo cargar este alojamiento", body: "Comprueba tu conexión y vuelve a intentarlo." },
    seeRooms: "Ver habitaciones", seeOffer: "Ver la oferta",
    sections: "Secciones del alojamiento", location: "Ubicación",
    booking: {
      label: "Tu estancia", from: "Desde", perNight: "por noche", total: "Total",
      chooseDates: "Elegir las fechas", addDates: "Añade tus fechas para ver el total de tu estancia.",
      line: (price, nights) => `${price} × ${nights}`, roomsLine: (rooms, type) => (type ? `${rooms}, ${type}` : rooms),
      maxGuests: (n) => `Este alojamiento admite ${n} huésped${n > 1 ? "es" : ""} como máximo. Cambia el número de huéspedes para reservar.`,
      full: "Esta habitación está completa. Elige otra.", allFull: "Por ahora no queda ninguna habitación en este alojamiento.",
      promotion: "Si el propietario tiene una promoción en estas fechas, se descuenta en el siguiente paso.",
      datesInvalid: "La salida debe ser posterior a la llegada.",
      converted: (amount) => `≈ ${amount} en tu moneda, convertido del precio en francos CFA de arriba.`,
    },
    rooms: {
      none: "Este alojamiento aún no ha detallado sus habitaciones. Elige tus fechas y huéspedes en el resumen para reservarlo.",
      header: { room: "Habitación", terms: "Condiciones", price: "Precio de tu estancia" },
      choose: "Elegir", chosen: "Elegida", fits: (n) => `${n} huésped${n > 1 ? "es" : ""}`,
      noTerms: "Condiciones indicadas antes del pago.", forNights: (nights) => `por ${nights}`,
    },
    amenitiesAll: "Ver todos los servicios", amenitiesNone: "El establecimiento aún no ha indicado sus servicios.",
    policiesNone: "El establecimiento aún no ha indicado sus condiciones.",
    gallery: { open: "Abrir la foto", previous: "Foto anterior", next: "Foto siguiente", all: "Ver todas las fotos", none: "Aún no hay fotos", title: "Galería de fotos", thumbnails: "Miniaturas", photo: "Foto" },
  },
  pt: {
    notFound: { title: "Este alojamento já não está publicado", body: "Pode ter sido retirado. Há outros alojamentos à sua espera.", action: "Ver alojamentos" },
    loadError: { title: "Não foi possível carregar este alojamento", body: "Verifique a ligação e tente novamente." },
    seeRooms: "Ver quartos", seeOffer: "Ver a oferta",
    sections: "Secções do alojamento", location: "Localização",
    booking: {
      label: "A sua estadia", from: "A partir de", perNight: "por noite", total: "Total",
      chooseDates: "Escolher as datas", addDates: "Adicione as suas datas para ver o total da estadia.",
      line: (price, nights) => `${price} × ${nights}`, roomsLine: (rooms, type) => (type ? `${rooms}, ${type}` : rooms),
      maxGuests: (n) => `Este alojamento recebe no máximo ${n} hóspede${n > 1 ? "s" : ""}. Altere o número de hóspedes para reservar.`,
      full: "Este quarto está esgotado. Escolha outro.", allFull: "De momento não há nenhum quarto disponível neste alojamento.",
      promotion: "Se o proprietário tiver uma promoção nestas datas, é descontada no passo seguinte.",
      datesInvalid: "A saída tem de ser depois da chegada.",
      converted: (amount) => `≈ ${amount} na sua moeda, convertido do preço em francos CFA acima.`,
    },
    rooms: {
      none: "Este alojamento ainda não descreveu os quartos. Escolha as datas e os hóspedes no resumo para o reservar.",
      header: { room: "Quarto", terms: "Condições", price: "Preço da sua estadia" },
      choose: "Escolher", chosen: "Escolhido", fits: (n) => `${n} hóspede${n > 1 ? "s" : ""}`,
      noTerms: "Condições indicadas antes do pagamento.", forNights: (nights) => `para ${nights}`,
    },
    amenitiesAll: "Ver todas as comodidades", amenitiesNone: "O estabelecimento ainda não indicou as comodidades.",
    policiesNone: "O estabelecimento ainda não indicou as condições.",
    gallery: { open: "Abrir a foto", previous: "Foto anterior", next: "Foto seguinte", all: "Ver todas as fotos", none: "Ainda sem fotos", title: "Galeria de fotos", thumbnails: "Miniaturas", photo: "Foto" },
  },
  ar: {
    notFound: { title: "مكان الإقامة هذا لم يعد معروضًا", body: "ربما تمت إزالته. هناك أماكن إقامة أخرى بانتظارك.", action: "عرض أماكن الإقامة" },
    loadError: { title: "تعذر تحميل مكان الإقامة هذا", body: "تحقق من اتصالك، ثم حاول مرة أخرى." },
    seeRooms: "عرض الغرف", seeOffer: "عرض العرض",
    sections: "أقسام مكان الإقامة", location: "الموقع",
    booking: {
      label: "إقامتك", from: "ابتداءً من", perNight: "لكل ليلة", total: "الإجمالي",
      chooseDates: "اختر التواريخ", addDates: "أضف تواريخك لرؤية إجمالي إقامتك.",
      line: (price, nights) => `${price} × ${nights}`, roomsLine: (rooms, type) => (type ? `${rooms}، ${type}` : rooms),
      maxGuests: (n) => `يستقبل مكان الإقامة هذا ${n} نزلاء كحد أقصى. غيّر عدد النزلاء للحجز.`,
      full: "هذه الغرفة محجوزة بالكامل. اختر غرفة أخرى.", allFull: "لا توجد غرف متاحة في مكان الإقامة هذا حاليًا.",
      promotion: "إذا كان لدى المالك عرض على هذه التواريخ، فسيُخصم في الخطوة التالية.",
      datesInvalid: "يجب أن تكون المغادرة بعد الوصول.",
      converted: (amount) => `≈ ${amount} بعملتك، محوّلًا من السعر بالفرنك الأفريقي أعلاه.`,
    },
    rooms: {
      none: "لم يصف مكان الإقامة هذا غرفه بعد. اختر تواريخك وعدد النزلاء في الملخص لحجزه.",
      header: { room: "الغرفة", terms: "الشروط", price: "سعر إقامتك" },
      choose: "اختيار", chosen: "تم الاختيار", fits: (n) => `${n} نزلاء`,
      noTerms: "تظهر الشروط قبل الدفع.", forNights: (nights) => `لمدة ${nights}`,
    },
    amenitiesAll: "عرض كل المرافق", amenitiesNone: "لم تذكر المنشأة مرافقها بعد.",
    policiesNone: "لم تذكر المنشأة شروطها بعد.",
    gallery: { open: "فتح الصورة", previous: "الصورة السابقة", next: "الصورة التالية", all: "عرض كل الصور", none: "لا توجد صور بعد", title: "معرض الصور", thumbnails: "الصور المصغرة", photo: "صورة" },
  },
  zh: {
    notFound: { title: "该住宿已下线", body: "它可能已被移除。还有其他住宿可供选择。", action: "查看住宿" },
    loadError: { title: "无法加载该住宿", body: "请检查网络连接，然后重试。" },
    seeRooms: "查看客房", seeOffer: "查看报价",
    sections: "住宿各部分", location: "位置",
    booking: {
      label: "您的住宿", from: "起价", perNight: "每晚", total: "总价",
      chooseDates: "选择日期", addDates: "添加日期即可查看住宿总价。",
      line: (price, nights) => `${price} × ${nights}`, roomsLine: (rooms, type) => (type ? `${rooms}，${type}` : rooms),
      maxGuests: (n) => `该住宿最多可入住 ${n} 位住客。请修改住客人数后预订。`,
      full: "该房间已满。请选择其他房间。", allFull: "该住宿目前已无可订房间。",
      promotion: "如房东在这些日期有优惠，将在下一步扣除。",
      datesInvalid: "退房日期必须晚于入住日期。",
      converted: (amount) => `≈ ${amount}（您的货币），按上方西非法郎价格换算。`,
    },
    rooms: {
      none: "该住宿尚未详细介绍客房。请在摘要中选择日期和住客人数进行预订。",
      header: { room: "客房", terms: "条件", price: "住宿价格" },
      choose: "选择", chosen: "已选择", fits: (n) => `可住 ${n} 人`,
      noTerms: "付款前会显示相关条件。", forNights: (nights) => `${nights}`,
    },
    amenitiesAll: "查看全部设施", amenitiesNone: "该住宿尚未列出设施。",
    policiesNone: "该住宿尚未提供政策信息。",
    gallery: { open: "打开照片", previous: "上一张", next: "下一张", all: "查看全部照片", none: "暂无照片", title: "照片集", thumbnails: "缩略图", photo: "照片" },
  },
};
